import React from 'react';
import { useDeals } from '../../context/DataContext';
import { useAuth } from '../../context/AuthContext';
import { useInvoices } from '../../context/InvoicesContext';
import { dealTalentIds, getContentStatus, getContentStatusMeta, toggleContentStatus, isStaleDeal, isInvoicedDeal } from '../../lib/deals';
import { fmtMoney } from '../../lib/format';
import { Link as LinkIcon, ChevronDown, Repeat, Calendar } from 'lucide-react';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import InitialsAvatar from '../InitialsAvatar';
import ContentStatusChecklist from '../ContentStatusChecklist';
import { toast } from 'sonner';

const normalizeUrl = (u) => {
  if (!u) return '';
  const s = String(u).trim();
  if (!s) return '';
  return /^https?:\/\//i.test(s) ? s : `https://${s}`;
};

/**
 * DealCard — kanban board card.
 * Shows: Deal name + inline Content-status dropdown + £ value + Talent initials + Drive link.
 */
const DealCard = ({ deal, onClick, dragProps }) => {
  const { talentMap, updateDeal } = useDeals();
  const [delivDraft, setDelivDraft] = React.useState(deal.type || '');
  React.useEffect(() => { setDelivDraft(deal.type || ''); }, [deal.type]);
  const { refresh: refreshInvoices } = useInvoices();
  const { user } = useAuth();
  const isManager = ['manager', 'talent_manager'].includes(user?.role);
  const talents = dealTalentIds(deal).map((id) => talentMap[id]).filter(Boolean);
  const amount = Number(deal.value) || 0;
  const href = normalizeUrl(deal.link);
  const current = getContentStatus(deal);
  const meta = getContentStatusMeta(current);
  const invoiced = isInvoicedDeal(deal);
  const stale = isStaleDeal(deal);

  const toggle = async (id) => {
    const patch = toggleContentStatus(deal, id);
    const result = await updateDeal(deal.id, patch);
    if (result?._auto_invoice_created) {
      await refreshInvoices();
      toast.success('draft invoice created — content is live');
    }
    if (result?._marked_ready_to_invoice) {
      toast.success('marked ready to invoice', { description: 'now suggested on the talent invoicing tab' });
    } else if (id === 'content_live' && patch.content_statuses.includes('content_live') && (Number(deal.value) || 0) <= 0) {
      toast('content marked live', { description: 'add a £ rate to this deal so it can be suggested for invoicing' });
    }
  };

  return (
    <div
      {...dragProps}
      onClick={onClick}
      className={`group border rounded-lg p-2.5 hover:shadow-md hover:-translate-y-0.5 transition-all cursor-pointer relative overflow-hidden ${invoiced ? 'bg-[#d9f5e6] border-[#3fa172] hover:border-[#3fa172]' : stale ? 'bg-[#f2c6d9] border-[#c2537e] hover:border-[#c2537e]' : 'bg-white border-slate-200 hover:border-pink-400'}`}
      data-testid={`deal-card-${deal.id}`}
      title={invoiced ? 'invoiced' : stale ? 'no update in 5+ days' : undefined}
    >
      {stale && !invoiced && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: '#8a2c5b' }} data-testid={`deal-card-stale-dot-${deal.id}`} />
      )}
      {isManager && (deal.rate_review || deal.status_review) && (
        <span
          className="absolute top-1.5 left-1.5 z-10 text-[9px] font-bold px-1.5 py-0.5 rounded-full bg-red-500 text-white shadow"
          title={`talent changed the ${[deal.rate_review && 'rate', deal.status_review && 'stage'].filter(Boolean).join(' & ')} — open to confirm`}
          data-testid={`deal-card-review-badge-${deal.id}`}
        >
          check {[deal.rate_review && 'rate', deal.status_review && 'stage'].filter(Boolean).join(' & ')}
        </span>
      )}
      {invoiced && (
        <span className="absolute top-1.5 right-1.5 w-2 h-2 rounded-full" style={{ background: '#166534' }} data-testid={`deal-card-invoiced-dot-${deal.id}`} />
      )}
      <span className="absolute left-0 top-0 bottom-0 w-1 opacity-0 group-hover:opacity-100 transition-opacity" style={{ background: '#d394ab' }} />
      {/* Deal title + drive link inline */}
      <div className="flex items-start gap-2">
        <h4 className="flex-1 text-[13px] font-semibold text-slate-800 leading-snug line-clamp-2">{deal.title}</h4>
        {(deal.retainer && (deal.deliverables || []).length > 1) && (
          <span className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[9px] font-semibold bg-lilac-100 text-lilac-700 shrink-0" title={`${deal.deliverables.length} deliverables`} data-testid={`deal-card-retainer-${deal.id}`}>
            <Repeat className="w-2.5 h-2.5" /> retainer
          </span>
        )}
        {href && (
          <a
            href={href}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            onMouseDown={(e) => e.stopPropagation()}
            title={href}
            data-testid={`deal-card-link-${deal.id}`}
            className="mt-0.5 inline-flex items-center justify-center w-5 h-5 rounded border border-slate-200 bg-white text-slate-400 hover:border-pink-300 hover:text-pink-600 shrink-0"
          >
            <LinkIcon className="w-2.5 h-2.5" />
          </a>
        )}
      </div>

      {/* Content status — inline picker */}
      <div className="mt-1.5" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
        <Popover>
          <PopoverTrigger asChild>
            <button
              type="button"
              className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded-full text-[10px] font-medium hover:brightness-95 transition"
              style={{ background: meta.color + '22', color: meta.color }}
              data-testid={`deal-card-content-status-${deal.id}`}
            >
              <span className="w-1 h-1 rounded-full" style={{ background: meta.color }} />
              {meta.label}
              <ChevronDown className="w-2.5 h-2.5 opacity-70" />
            </button>
          </PopoverTrigger>
          <PopoverContent align="start" className="w-56 p-1">
            <ContentStatusChecklist deal={deal} isManager={isManager} onToggle={toggle} />
          </PopoverContent>
        </Popover>
      </div>

      {/* Live date + deliverables bubbles — on rate-confirmed & contracted deals; edit inline, saves everywhere */}
      {['rate_confirmed', 'contracted'].includes(deal.stage) && (
        <div className="mt-2 flex flex-wrap items-center gap-1" onClick={(e) => e.stopPropagation()} onMouseDown={(e) => e.stopPropagation()}>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-medium transition hover:brightness-95 ${deal.go_live_date ? 'bg-pink-100 text-pink-700' : 'bg-slate-100 text-slate-400'}`}
                data-testid={`deal-card-live-date-${deal.id}`}
              >
                <Calendar className="w-2.5 h-2.5" />
                {deal.go_live_date ? new Date(`${deal.go_live_date}T00:00`).toLocaleDateString('en-GB', { day: 'numeric', month: 'short' }) : 'live date'}
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-auto p-2">
              <input
                type="date"
                autoFocus
                value={deal.go_live_date || ''}
                onChange={(e) => updateDeal(deal.id, { go_live_date: e.target.value })}
                className="h-8 text-xs px-2 rounded border border-slate-200 focus:border-pink-300 focus:outline-none"
                data-testid={`deal-card-live-date-input-${deal.id}`}
              />
            </PopoverContent>
          </Popover>
          <Popover>
            <PopoverTrigger asChild>
              <button
                type="button"
                className={`inline-flex items-center gap-1 h-5 px-2 rounded-full text-[10px] font-medium max-w-[170px] transition hover:brightness-95 ${deal.type ? 'bg-lilac-100 text-lilac-700' : 'bg-slate-100 text-slate-400'}`}
                data-testid={`deal-card-deliverables-${deal.id}`}
              >
                <span className="truncate">{deal.type || 'deliverables'}</span>
              </button>
            </PopoverTrigger>
            <PopoverContent align="start" className="w-64 p-2">
              <label className="text-[10px] uppercase tracking-wide text-slate-500">deliverables</label>
              <input
                autoFocus
                value={delivDraft}
                onChange={(e) => setDelivDraft(e.target.value)}
                onBlur={() => { if ((delivDraft || '') !== (deal.type || '')) updateDeal(deal.id, { type: delivDraft }); }}
                placeholder="e.g. 2× Reels + 3 Stories"
                className="w-full h-8 text-xs px-2 mt-1 rounded border border-slate-200 focus:border-pink-300 focus:outline-none"
                data-testid={`deal-card-deliverables-input-${deal.id}`}
              />
            </PopoverContent>
          </Popover>
        </div>
      )}

      {/* £ value + talent initials */}
      <div className="mt-2 flex items-center justify-between gap-2">
        <span className="text-sm font-semibold" style={{ color: '#8a2c5b' }}>{fmtMoney(amount, deal.currency)}</span>
        {talents.length > 0 && (
          <div className="flex items-center -space-x-1.5">
            {talents.slice(0, 4).map((t) => (
              <div key={t.user_id} className="ring-2 ring-white rounded-full" title={t.name}>
                <InitialsAvatar name={t.name} size={18} />
              </div>
            ))}
            {talents.length > 4 && (
              <span className="ml-1 text-[10px] text-slate-500 font-medium">+{talents.length - 4}</span>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default DealCard;
